import './index.css'
import { createRoot } from 'react-dom/client'
import {
    BrowserRouter as Router,
    Redirect,
    Route,
    Switch,
} from 'react-router-dom'
import { Provider } from 'react-redux'
import { store } from './state'
import ThemeProvider from './providers/ThemeProvider'
import Header from './components/layout/header/Header'
import ActiveModal from './components/layout/ActiveModal'
import LoginPage from './components/pages/auth/login/LoginPage'
import VerifyEmailPage from './components/pages/auth/verify-email/VerifyEmailPage'
import NewCasePage from './components/pages/whistleblower/new-case/NewCasePage'
import ProtectedCaseworkerRoute from './components/routing/ProtectedCaseworkerRoute'
import ProtectedUnauthenticatedRoute from './components/routing/ProtectedUnauthenticatedRoute'
import ProtectedWhistleblowerRoute from './components/routing/ProtectedWhistleblowerRoute'
// import RegisterPage from './components/pages/auth/register/RegisterPage'
import { useTheme } from './hooks/useTheme'
import WhistleblowerAccessReportPage from './components/pages/whistleblower/access-report/WhistleblowerAccessReportPage'
import DashboardPage from './components/pages/partners/dashboard/DashboardPage'
import OrganizationalCode404 from './components/pages/404/OrganizationalCode404'
import ClientsPage from './components/pages/partners/clients/ClientsPage'
import StatisticsPage from './components/pages/partners/statistics/StatisticsPage'
import SingleCase from './components/pages/partners/single-case/SingleCase'
import SettingsPage from './components/pages/shared/settings/SettingsPage'
import { useEffect, useState, useRef } from 'react'
import { useActions, useIsCaseworker, useTypedSelector } from './hooks'
import WhistleblowerUnauthenticated from './components/pages/auth/whistleblower-unauthenticated/WhistleblowerUnauthenticated'
import ProtectedUnauthenticatedWhistleblowerRoute from './components/routing/ProtectedUnauthenticatedWhistleblowerRoute'
import WhistleblowerLoading from './components/pages/loading/WhistleblowerLoading'
import CasesPage from './components/pages/partners/cases/CasesPage'
import {
    getFirstSubdomain,
    getSecondSubdomain,
    getUrlAfterAppSubdomain,
} from './helpers/routing'
import ProtectedAdminRoute from './components/routing/ProtectedAdminRoute'
import AdminPage from './components/pages/admin/AdminPage'
import ClientInformationPage from './components/pages/clients/ClientInformationPage'
import MagicLogin from './components/pages/redirects/MagicLogin'
import Logout from './components/pages/redirects/Logout'
import AddNewClient from './components/pages/partners/add-new-client/AddNewClient'
import ProtectedClientRoute from './components/routing/ProtectedClientRoute'
import ProtectedPartnerRoute from './components/routing/ProtectedPartnerRoute'
import config from './config'
import Echo from 'laravel-echo'
// import ToSmallScreenModal from './components/layout/ToSmallScreenModal'
import ResetPasswordPage from './components/pages/auth/reset-password/ResetPasswordPage'
import ProtectedContactRoute from './components/routing/ProtectedContactRoute'
import ContactDashboardPage from './components/pages/contact-users/dashboard/ContactDashboardPage'
import ContactAllPages from './components/pages/contact-users/all-cases/ContactAllCases'
import ContactClosedCasesPage from './components/pages/contact-users/closed-cases/ContactClosedCasesPage'
import NewestRegisterPage from './components/pages/auth/newest-register/NewestRegisterPage'
import ClientHomepage from './components/pages/clients/ClientHomepage'
import HowItWorksPage from './components/pages/whistleblower/how-it-works/HowItWorksPage'
import { useLocation } from 'react-router-dom'
import OnboardingPage from './components/pages/clients/OnboardingPage'
import axios from 'axios'
import ChangedEmailVerify from './components/pages/redirects/ChangedEmailVerify'
import WhistleblowerPolicyResolver from './components/pages/whistleblower/whistleblower-policy/WhistleblowerPolicyResolver'
import InitIntegrationPage from './components/pages/integrations/InitIntegrationPage'
import ResolveIntegrationPage from './components/pages/integrations/ResolveIntegrationPage'
import ReportingChannel from './components/pages/whistleblower/homepage/ReportingChannel'
import HowItWorks from './components/pages/whistleblower/how-it-works/HowItWorks'
import SetupAccount from './components/pages/setup-account/SetupAccountPage'
import { useIdleTimer } from 'react-idle-timer'
import InactiveModal from './components/layout/InactiveModal'
import DeletedCasePage from './components/pages/contact-users/deleted-cases/DeletedCasePage'
import ReportingChannelRedirect from './components/pages/redirects/ReportingChannelRedirect'
import IntegrationModalResolver from './components/integrations/modals/IntegrationModalResolver'

const Base = () => {
    return (
        <Router>
            <Provider store={store}>
                <ThemeProvider>
                    <div style={{ position: 'relative', overflowX: 'hidden' }}>
                        <ActiveModal />
                        {window.location.pathname !== '/new-register' && (
                            <Header />
                        )}
                        <App />
                    </div>
                </ThemeProvider>
            </Provider>
        </Router>
    )
}

const PartnerRoutes = () => {
    const isCaseworker = useIsCaseworker()

    return (
        <>
            {isCaseworker ? (
                <ProtectedCaseworkerRoute
                    exact
                    path="/"
                    component={DashboardPage}
                />
            ) : (
                <Route exact path="/">
                    <Redirect to="/clients" />
                </Route>
            )}

            {isCaseworker && (
                <ProtectedCaseworkerRoute
                    exact
                    path="/cases"
                    component={CasesPage}
                />
            )}

            <ProtectedPartnerRoute
                exact
                path="/clients"
                component={ClientsPage}
            />

            {isCaseworker && (
                <ProtectedCaseworkerRoute
                    exact
                    path="/statistics"
                    component={StatisticsPage}
                />
            )}

            <ProtectedCaseworkerRoute
                exact
                path="/report/:id"
                component={SingleCase}
            />

            <ProtectedCaseworkerRoute
                exact
                path="/deleted-report/:id"
                component={DeletedCasePage}
            />

            <ProtectedPartnerRoute
                exact
                path="/settings"
                component={SettingsPage}
            />

            <ProtectedPartnerRoute
                exact
                path="/add-new-client"
                component={AddNewClient}
            />
        </>
    )
}

const App = () => {
    const { env, pusherKey } = config
    const theme = useTheme()
    const [platform, setPlatform] = useState<string | null>('')
    const {
        getCategories,
        addCountries,
        getSelf,
        login,
        getNotifications,
        showLatestNotification,
        getPaymentInformation,
        contactGetOrganizations,
        pingAPI,
        changeLanguage,
        getIdentity,
        getSpokenLanguages,
    } = useActions()

    // Configure axios headers
    useEffect(() => {
        axios.defaults.headers.common['X-Frame-Options'] = 'DENY'
        axios.defaults.headers.common['X-Content-Type-Options'] = 'nosniff'
        axios.defaults.headers.common['Referrer-Policy'] =
            'strict-origin-when-cross-origin'
    }, [])

    useEffect(() => {
        window.Pusher = require('pusher-js')

        window.Echo = new Echo({
            broadcaster: 'pusher',
            key: pusherKey,
            cluster: 'eu',
            forceTLS: true,
        })
        // eslint-disable-next-line
    }, [])

    const { selectedLanguage } = useTypedSelector((state) => state.identity)

    useEffect(() => {
        if (selectedLanguage) {
            changeLanguage(selectedLanguage)
        }
        //eslint-disable-next-line
    }, [selectedLanguage])

    const { user } = useTypedSelector((state) => state.auth)

    const [isInactiveModalActive, setIsInactiveModalActive] = useState(false)

    const handleOnIdle = () => {
        userRef && setIsInactiveModalActive(true)
    }

    const userRef = useRef(user)

    useEffect(() => {
        userRef.current = user
    }, [user])

    useIdleTimer({
        onIdle: handleOnIdle,
        timeout: 1000 * 60 * 13.5,
        crossTab: true,
        debounce: 500,
    })

    useEffect(() => {
        if (user) {
            getIdentity()
        }
        //eslint-disable-next-line
    }, [user])

    // Set the schedule to ping the backend every minute to keep the API token alive
    useEffect(() => {
        if (user) {
            setInterval(() => {
                pingAPI()
            }, 60000)
        }

        // eslint-disable-next-line
    }, [user])

    const { organization } = useTypedSelector((state) => state.organization)

    const { shouldShowTrialInformation, isTrialExpired } = useTypedSelector(
        (state) => state.payment
    )

    useEffect(() => {
        if (user) {
            window.Echo.channel(`notification-user-${user.userId}`).listen(
                '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
                (notification: any) => {
                    showLatestNotification(notification)
                }
            )
        }
        // eslint-disable-next-line
    }, [user])

    // Get organization for contact
    useEffect(() => {
        if (!user || organization) {
            return
        }

        if (user.roles.includes('contact') && !user.roles.includes('client')) {
            contactGetOrganizations(user.userId)
        }

        // eslint-disable-next-line
    }, [user, organization])

    useEffect(() => {
        if (user) {
            getNotifications()
        }
        // eslint-disable-next-line
    }, [user])

    useEffect(() => {
        const subDomain = window.location.host.split('.')[1]
        if (
            subDomain === 'partner' ||
            subDomain === 'whistleblower' ||
            subDomain === 'client' ||
            subDomain === 'admin' ||
            subDomain === 'contact'
        ) {
            setPlatform(subDomain)
        } else if (subDomain === 'undefined') {
        } else if (subDomain === 'walor') {
            setPlatform(null)
        }
    }, [])

    useEffect(() => {
        if (env === 'local') {
            const userType = getSecondSubdomain()
            if (
                userType === 'admin' ||
                userType === 'partner' ||
                userType === 'caseworker' ||
                userType === 'client' ||
                userType === 'contact'
            ) {
                const isLoggedIn = localStorage.getItem('isLoggedIn')
                if (isLoggedIn) {
                    const user = localStorage.getItem('user')
                    if (user) {
                        const userJson = JSON.parse(user)

                        login(userJson)
                    }
                }
            }
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (getFirstSubdomain() !== 'app') {
            window.location.replace(`http://app.${getUrlAfterAppSubdomain()}`)
        }
    }, [])

    // useEffect(() => {
    //     if (getFirstSubdomain() === 'app') {
    //         const minusApp = window.location.host.replace('app.', '')
    //         if (minusApp === getUrlAfterAppSubdomain()) {
    //             const isLoggedIn = localStorage.getItem('isLoggedIn')
    //             const userType = localStorage.getItem('userType')
    //             const correctUser =
    //                 userType === 'admin' ||
    //                 userType === 'partner' ||
    //                 userType === 'caseworker'

    //             const subdomain =
    //                 userType === 'caseworker' ? 'partner' : userType
    //             if (isLoggedIn && correctUser) {
    //                 window.location.replace(
    //                     `http://app.${subdomain}.${getUrlAfterAppSubdomain()}`
    //                 )
    //             }
    //         }
    //     }
    // }, [])

    useEffect(() => {
        getCategories()
        addCountries()
        getSpokenLanguages()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        const userType = getSecondSubdomain()
        if (userType === 'partner') {
            if (user) {
                getSelf()
            }
        }
        // eslint-disable-next-line
    }, [user])

    useEffect(() => {
        if (!user || user.temp) {
            return
        }

        if (user.roles.includes('client')) {
            getPaymentInformation(user.userId)
        }
        // eslint-disable-next-line
    }, [user])

    const location = useLocation()

    return (
        <div
            className="app"
            style={{
                background: theme.platformGrey,
                minHeight: 'calc(100vh - 8.5rem)',
                margin: '0 auto',
            }}
        >
            <div
                style={{
                    maxWidth: '160rem',
                    margin: '0 auto',
                    filter:
                        isTrialExpired &&
                        shouldShowTrialInformation &&
                        location.pathname !== '/settings'
                            ? 'blur(4px)'
                            : '',
                }}
            >
                {/* {platform !== 'whistleblower' && <ToSmallScreenModal />} */}
                <Switch>
                    <Route
                        path="/caseworker-onboarding/:token"
                        component={SetupAccount}
                    />
                    <Route path="/onboarding" component={OnboardingPage} />
                    <Route
                        path="/integrations/:uuid"
                        component={InitIntegrationPage}
                    />
                    <Route
                        path="/resolve-integration/:uuid"
                        component={ResolveIntegrationPage}
                    />
                    <Route
                        path="/resolve-integration/"
                        component={ResolveIntegrationPage}
                    />
                    {/*<Route path="/new-register" component={NewRegisterPage} /> */}
                    <Route
                        exact
                        path={[
                            '/register/',
                            '/register/integrations',
                            '/register/:partnerId',
                        ]}
                        component={NewestRegisterPage}
                    />
                    {/* Routes accessable before login in */}
                    <Route
                        path="/magic-login/:magicLoginToken"
                        component={MagicLogin}
                    />
                    {!platform && (
                        <>
                            {/* <ProtectedUnauthenticatedRoute
                            path="/onboarding"
                            component={RegisterPage}
                        /> */}

                            <ProtectedUnauthenticatedRoute
                                exact
                                path={['/', '/login/integrations']}
                                component={LoginPage}
                            />
                            <ProtectedUnauthenticatedRoute
                                exact
                                path="/reset-password/:token"
                                component={ResetPasswordPage}
                            />
                            <ProtectedUnauthenticatedRoute
                                path="/verify-email/:requestNumber/:expires/:hash/:signature"
                                component={VerifyEmailPage}
                            />
                            <Route
                                path="/changed-email-verify/:verificationToken"
                                component={ChangedEmailVerify}
                            />
                            <Route exact path="/logout" component={Logout} />
                        </>
                    )}

                    {platform === 'whistleblower' && (
                        <>
                            <Route
                                exact
                                path="/organization/:id/"
                                component={ReportingChannelRedirect}
                            />

                            <Route
                                exact
                                path="/organization/:id/whistleblower"
                                component={ReportingChannel}
                            />

                            <Route
                                exact
                                path="/organization/:id/policy"
                                component={WhistleblowerPolicyResolver}
                            />
                            <Route
                                exact
                                path="/organization/:id/how-it-works"
                                component={HowItWorks}
                            />

                            <Route
                                exact
                                path="/not-found/organizational-code"
                                component={OrganizationalCode404}
                            />

                            {/* Routes accessable for whistleblowers */}
                            <ProtectedWhistleblowerRoute
                                path="/organization/:id/create-report"
                                component={NewCasePage}
                            />
                            <ProtectedWhistleblowerRoute
                                path="/whistleblower-policy"
                                component={WhistleblowerPolicyResolver}
                            />
                            <ProtectedWhistleblowerRoute
                                path="/how-it-works"
                                component={HowItWorksPage}
                            />
                            <ProtectedWhistleblowerRoute
                                path="/organization/:id/access-report"
                                component={WhistleblowerAccessReportPage}
                            />
                            <ProtectedUnauthenticatedWhistleblowerRoute
                                path="/not-assigned-to-company"
                                component={WhistleblowerUnauthenticated}
                            />
                            <ProtectedUnauthenticatedWhistleblowerRoute
                                path="/loading"
                                component={WhistleblowerLoading}
                            />
                        </>
                    )}

                    {platform === 'partner' && <PartnerRoutes />}

                    <>
                        {platform === 'client' && (
                            <>
                                {isInactiveModalActive && (
                                    <InactiveModal
                                        closeModal={() =>
                                            setIsInactiveModalActive(false)
                                        }
                                    />
                                )}
                                {/*         <IntegrationModal />*/}
                                {/*       <HrOnModalConfirmIntegrationModal />*/}
                                <ProtectedClientRoute
                                    exact
                                    path="/"
                                    component={ClientHomepage}
                                />
                                <ProtectedClientRoute
                                    exact
                                    path="/settings"
                                    component={SettingsPage}
                                />
                            </>
                        )}
                        {platform === 'contact' && (
                            <>
                                {isInactiveModalActive && (
                                    <InactiveModal
                                        closeModal={() =>
                                            setIsInactiveModalActive(false)
                                        }
                                    />
                                )}
                                <ProtectedContactRoute
                                    exact
                                    path="/"
                                    component={ContactDashboardPage}
                                />
                                {/*<ProtectedContactRoute
                                exact
                                path="/"
                                component={ClientHomepage}
                            /> */}
                                <ProtectedContactRoute
                                    exact
                                    path="/report/:id"
                                    component={SingleCase}
                                />
                                <ProtectedContactRoute
                                    exact
                                    path="/deleted-report/:id"
                                    component={DeletedCasePage}
                                />
                                <ProtectedContactRoute
                                    exact
                                    path="/cases"
                                    component={ContactAllPages}
                                />
                                <ProtectedContactRoute
                                    exact
                                    path="/closed-cases"
                                    component={ContactClosedCasesPage}
                                />
                                <ProtectedContactRoute
                                    exact
                                    path="/settings"
                                    component={SettingsPage}
                                />
                                <ProtectedContactRoute
                                    exact
                                    path="/statistics"
                                    component={StatisticsPage}
                                />
                                {user?.roles.includes('client') && (
                                    <>
                                        {/*     <IntegrationModal />*/}
                                        <IntegrationModalResolver />

                                        {/*     <HrOnModalConfirmIntegrationModal />*/}
                                        <ProtectedContactRoute
                                            exact
                                            path="/organization"
                                            component={ClientInformationPage}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>

                    {platform === 'contact' && (
                        <>
                            <ProtectedContactRoute
                                exact
                                path="/"
                                component={ContactDashboardPage}
                            />
                            <ProtectedContactRoute
                                exact
                                path="/report/:id"
                                component={SingleCase}
                            />
                            <ProtectedContactRoute
                                exact
                                path="/cases"
                                component={ContactAllPages}
                            />
                            <ProtectedContactRoute
                                exact
                                path="/closed-cases"
                                component={ContactClosedCasesPage}
                            />
                            <ProtectedContactRoute
                                exact
                                path="/settings"
                                component={SettingsPage}
                            />
                            {user?.roles.includes('client') && (
                                <>
                                    <ProtectedContactRoute
                                        exact
                                        path="/organization"
                                        component={ClientInformationPage}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {platform === 'admin' && (
                        <>
                            <ProtectedAdminRoute
                                exact
                                path="/"
                                component={AdminPage}
                            />
                        </>
                    )}
                </Switch>
            </div>
        </div>
    )
}

const container = document.getElementById('root')!
const root = createRoot(container)
root.render(<Base />)
